import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gift, Clock, Check, Award, Wallet, ArrowRight, AlertCircle } from 'lucide-react';
import { userApi, settingsApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';

export default function UserPromotions() {
  const [promotions, setPromotions] = useState([]);
  const [promoWallet, setPromoWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [blcPrice, setBlcPrice] = useState(0.027);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [promoRes, walletRes, settingsRes] = await Promise.all([
        userApi.getPromotionProgress(),
        userApi.getPromotionWallet(),
        settingsApi.get()
      ]);
      setPromotions(promoRes.data.promotions || []);
      setPromoWallet(walletRes.data);
      setBlcPrice(settingsRes.data.settings?.blc_price || 0.027);
    } catch (error) {
      console.error('Error fetching promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransferToCash = async () => {
    setTransferring(true);
    try {
      const response = await userApi.transferPromoToCash();
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const usdToBLC = (usd) => blcPrice > 0 ? (Math.abs(usd) / blcPrice).toFixed(2) : '0.00';

  return (
    <div className="space-y-8" data-testid="promotions-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-pink-500/10">
          <Gift className="w-6 h-6 text-pink-400" />
        </div>
        <div>
          <h1 className="text-2xl text-white font-medium">Promotions & Rewards</h1>
          <p className="text-sm text-zinc-500">Complete targets to earn bonus rewards</p>
        </div>
      </div>

      {/* Promotion Wallet Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6 border border-cyan-500/30"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-cyan-500/10">
              <Wallet className="w-8 h-8 text-cyan-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase">Promotion Wallet</p>
              <p className="text-3xl text-cyan-400 font-bold">${(promoWallet?.promotion_wallet || 0).toFixed(2)}</p>
              <p className="text-xs text-cyan-400/70">{usdToBLC(promoWallet?.promotion_wallet || 0)} BLC</p>
              <p className="text-xs text-zinc-400 mt-1">
                {(promoWallet?.pending_rewards?.length || 0)} pending reward(s)
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              onClick={handleTransferToCash}
              disabled={transferring || (promoWallet?.promotion_wallet || 0) <= 0}
              className="gold-btn"
              data-testid="transfer-promo-btn"
            >
              {transferring ? (
                <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Transfer to Cash Wallet
                </>
              )}
            </Button>
            <p className="text-[10px] text-zinc-500 text-center">
              Available after promotion ends
            </p>
          </div>
        </div>

        {/* Pending Rewards List */}
        {promoWallet?.pending_rewards?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-xs text-zinc-400 mb-2">Pending Rewards (will transfer to Cash after promotion ends)</p>
            <div className="space-y-2">
              {promoWallet.pending_rewards.map((reward) => (
                <div key={reward.id} className="flex items-center justify-between p-2 rounded bg-black/30">
                  <div className="flex items-center gap-2">
                    <Gift className="w-4 h-4 text-pink-400" />
                    <span className="text-sm text-white">{reward.promotion_name}</span>
                  </div>
                  <span className="text-sm text-cyan-400 font-medium">${reward.amount.toFixed(2)} <span className="text-cyan-400/70 text-xs">({usdToBLC(reward.amount)} BLC)</span></span>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Important Notice */}
      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-400 font-medium text-sm">How Promotion Rewards Work</p>
            <ul className="text-xs text-amber-200 mt-1 space-y-1">
              <li>• Only referrals who <strong>activate staking packages</strong> count towards targets</li>
              <li>• When you achieve 100% of targets, reward is added to your Promotion Wallet</li>
              <li>• Rewards can be transferred to Cash Wallet after promotion end date</li>
              <li>• Cash Wallet funds can be withdrawn on allowed dates or used for staking</li>
            </ul>
          </div>
        </div>
      </div>

      {promotions.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Gift className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2">No Active Promotions</h3>
          <p className="text-zinc-500">Check back later for new promotions and rewards!</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {promotions.map((item, idx) => {
            const promo = item.promotion;
            const progress = item.progress || {};
            const isCompleted = Object.values(progress).every(p => p.completed);
            
            return (
              <motion.div
                key={promo.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`glass-card overflow-hidden ${
                  item.claimed ? 'opacity-60' : ''
                }`}
              >
                {/* Banner */}
                {promo.banner ? (
                  <div className="h-32 overflow-hidden">
                    <img 
                      src={promo.banner} 
                      alt={promo.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-24 bg-gradient-to-r from-amber-500/20 to-purple-500/20 flex items-center justify-center">
                    <Award className="w-12 h-12 text-amber-400" />
                  </div>
                )}

                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-white font-medium text-lg">{promo.name}</h3>
                      <p className="text-amber-400 text-2xl font-bold mt-1">
                        ${promo.reward_amount}
                        <span className="text-amber-400/70 text-sm ml-2">({usdToBLC(promo.reward_amount)} BLC)</span>
                      </p>
                    </div>
                    {item.claimed ? (
                      <span className="badge badge-success">
                        <Check className="w-3 h-3 mr-1" /> Claimed
                      </span>
                    ) : item.reward_pending ? (
                      <span className="badge badge-info">
                        <Wallet className="w-3 h-3 mr-1" /> In Promo Wallet
                      </span>
                    ) : isCompleted ? (
                      <span className="badge badge-warning">
                        Processing...
                      </span>
                    ) : (
                      <span className="badge badge-info">
                        In Progress
                      </span>
                    )}
                  </div>

                  {/* Requirements Progress */}
                  <div className="space-y-3">
                    {Object.entries(progress).map(([key, value]) => (
                      <div key={key}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-zinc-400 capitalize">
                            {key.replace('_', ' ')}
                          </span>
                          <span className={value.completed ? 'text-green-400' : 'text-white'}>
                            {value.current}/{value.required}
                            {value.completed && <Check className="w-3 h-3 inline ml-1" />}
                          </span>
                        </div>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill"
                            style={{ 
                              width: `${Math.min(100, (value.current / value.required) * 100)}%`,
                              background: value.completed 
                                ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                                : undefined
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Dates */}
                  <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2 text-xs text-zinc-500">
                    <Clock className="w-3 h-3" />
                    <span>
                      {new Date(promo.start_date).toLocaleDateString()} - {new Date(promo.end_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
